import { DownloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Empty, Flex, Form, Select, Typography } from "antd";
import type { Dayjs } from "dayjs";
import { useEffect, useMemo } from "react";
import { connect, type DispatchProp } from "react-redux";
import { Card } from "../../common/components/Card.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import {
  type ProjectReportFilters,
  reportsActions,
} from "../../features/reports/reportsSlice.ts";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import styles from "./ProjectReportPage.module.css";
import { WorkOrderPeopleTable } from "./WorkOrderPeopleTable.tsx";
import { WorkOrderTotalsStatistics } from "./WorkOrderTotalsStatistics.tsx";

type FilterFormValues = {
  project?: string;
  period?: [Dayjs | null, Dayjs | null] | null;
};

const API_DATE_FORMAT = "YYYY-MM-DD";

const toReportFilters = (values: FilterFormValues): ProjectReportFilters | null =>
  values.project
    ? {
        project: values.project,
        dateFrom: values.period?.[0]?.format(API_DATE_FORMAT),
        dateTo: values.period?.[1]?.format(API_DATE_FORMAT),
      }
    : null;

type ProjectReportPageProps = ReturnType<typeof withProjectReport> & DispatchProp;

export const ProjectReportPage = (props: ProjectReportPageProps) => {
  const { intl, dispatch, projects, summary, summaryStatus, downloadStatus } = props;
  const [form] = Form.useForm<FilterFormValues>();
  const selectedProject = Form.useWatch("project", form);

  useEffect(() => {
    return () => {
      dispatch(reportsActions.clearReports());
    };
  }, [dispatch]);

  const projectOptions = useMemo(
    () =>
      (projects ?? [])
        .map((project) => ({ value: project.id, label: project.name ?? "—" }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [projects]
  );

  const summaryLoading = summaryStatus === "loading";
  const hasSummary = summary !== null;
  const statisticLoading = summaryLoading && !hasSummary;

  const peopleGroups = [
    { titleId: "work_order_totals_technicians", people: summary?.technicians },
    { titleId: "project_report_coordinators", people: summary?.coordinators },
    { titleId: "project_report_managers", people: summary?.managers },
  ];

  return (
    <ConnectedAppLayout>
      <PageTitleContainer
        title={intl.formatMessage({ id: "project_report_title" })}
        subtitle={intl.formatMessage({ id: "project_report_subtitle" })}
        extra={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloadStatus === "loading"}
            disabled={!selectedProject || !hasSummary || summaryLoading}
            onClick={() => dispatch(reportsActions.downloadProjectReport())}
          >
            {intl.formatMessage({ id: "project_report_download_excel" })}
          </Button>
        }
      >
        <Card>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, allValues) => {
              const filters = toReportFilters(allValues);
              dispatch(
                filters
                  ? reportsActions.fetchProjectReportSummary(filters)
                  : reportsActions.clearReports()
              );
            }}
          >
            <Flex gap="middle" wrap align="end">
              <Form.Item
                name="project"
                label={intl.formatMessage({ id: "project_report_filter_project" })}
                className={styles.filterItem}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={projects === undefined}
                  placeholder={intl.formatMessage({
                    id: "project_report_choose_project",
                  })}
                  options={projectOptions}
                />
              </Form.Item>
              <Form.Item
                name="period"
                label={intl.formatMessage({ id: "project_report_filter_period" })}
                tooltip={intl.formatMessage({ id: "project_report_filter_period_hint" })}
                className={styles.filterItem}
              >
                <DatePicker.RangePicker allowEmpty={[true, true]} />
              </Form.Item>
            </Flex>
          </Form>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: "work_order_totals_counting_hint" })}
          </Typography.Text>
        </Card>

        {!selectedProject ? (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({ id: "project_report_no_project" })}
            />
          </Card>
        ) : summaryStatus === "failed" ? (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({ id: "project_report_load_failed" })}
            />
          </Card>
        ) : (
          <>
            <Card>
              <WorkOrderTotalsStatistics
                intl={intl}
                totals={summary}
                loading={statisticLoading}
              />
            </Card>

            <Flex gap="middle" wrap>
              {peopleGroups.map(({ titleId, people }) => (
                <Card
                  key={titleId}
                  title={intl.formatMessage({ id: titleId })}
                  className={styles.peopleCard}
                >
                  <WorkOrderPeopleTable
                    intl={intl}
                    people={people}
                    loading={summaryLoading}
                  />
                </Card>
              ))}
            </Flex>
          </>
        )}
      </PageTitleContainer>
    </ConnectedAppLayout>
  );
};

const withProjectReport = (state: RootState) => ({
  intl: state.intl.datetimeIntl,
  projects: state.ticketData.projects,
  summary: state.reports.projectReportSummary,
  summaryStatus: state.reports.projectReportSummaryStatus,
  downloadStatus: state.reports.projectReportDownloadStatus,
});

export const ConnectedProjectReportPage = connect(withProjectReport)(ProjectReportPage);
export default ConnectedProjectReportPage;
